import React, { useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/styles";
import {
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@material-ui/core";
import { useSelector } from "react-redux";
import moment from "moment";
import {
	Page,
	PageContent,
	PageHeader,
	ScrollToTopOnMount,
} from "frame/components";
import { useRouter } from "utils";
import { safetyWiseApi } from "utils/safetyWiseApi";
import { ACCOUNT_REPORTS_ROUTE } from "features/account/actions/types";
import { formatLinkedUsageSource } from "features/equipment/utils/equipment-usage-display";
import { unitLabel } from "features/equipment/utils/equipment-usage-math";

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
	},
	summary: {
		marginBottom: theme.spacing(2),
	},
	tableWrap: {
		overflowX: "auto",
	},
	empty: {
		padding: theme.spacing(3),
		color: theme.palette.text.secondary,
	},
}));

const formatReading = (value) =>
	value === null || value === undefined || value === "" ? "—" : value;

const EquipmentUsageReport = () => {
	const classes = useStyles();
	const { location, history } = useRouter();
	const organisationId = useSelector(
		({ organisations }) => organisations.currentOrganisation
	);
	const featureFlags = useSelector(({ featureFlags }) => featureFlags);
	const usageRecordingEnabled =
		featureFlags?.equipment === true &&
		featureFlags?.equipmentUsageRecording === true;
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	const params = useMemo(() => new URLSearchParams(location.search), [
		location.search,
	]);
	const from = params.get("from");
	const to = params.get("to");
	const siteId = params.get("siteId");

	useEffect(() => {
		if (
			featureFlags &&
			!featureFlags.fetch?.loading &&
			!usageRecordingEnabled
		) {
			history.replace(ACCOUNT_REPORTS_ROUTE);
		}
	}, [featureFlags, usageRecordingEnabled, history]);

	useEffect(() => {
		if (!organisationId || !usageRecordingEnabled) return;
		setLoading(true);
		setError("");
		const query = new URLSearchParams();
		if (from) query.set("from", from);
		if (to) query.set("to", to);
		if (siteId && siteId !== "all") query.set("siteId", siteId);
		safetyWiseApi
			.get(`${organisationId}/equipment-usage?${query.toString()}`)
			.then((res) => setRows(Array.isArray(res.data) ? res.data : []))
			.catch((err) => {
				setRows([]);
				setError(
					err?.response?.data?.message ||
						"Unable to load equipment usage."
				);
			})
			.finally(() => setLoading(false));
	}, [organisationId, from, to, siteId, usageRecordingEnabled]);

	const totals = useMemo(() => {
		const byEquipment = {};
		rows.forEach((row) => {
			const key = row.equipmentName || "Unknown";
			if (!byEquipment[key]) {
				byEquipment[key] = { units: 0, unitType: row.unitType };
			}
			byEquipment[key].units += Number(row.unitsUsed) || 0;
		});
		return byEquipment;
	}, [rows]);

	return (
		<Page className={classes.root} title="Equipment Usage Report">
			<ScrollToTopOnMount />
			<PageHeader
				canGoBack
				title="Equipment Usage Report"
				backActionPath={ACCOUNT_REPORTS_ROUTE}
			/>
			<PageContent pad>
				{error && (
					<Typography color="error" className={classes.summary}>
						{error}
					</Typography>
				)}
				{!loading && Object.keys(totals).length > 0 && (
					<Typography className={classes.summary}>
						{Object.entries(totals)
							.map(
								([name, info]) =>
									`${name}: ${info.units} ${unitLabel(
										info.unitType
									)}`
							)
							.join(" · ")}
					</Typography>
				)}
				<Paper className={classes.tableWrap}>
					{!loading && rows.length === 0 ? (
						<Typography className={classes.empty}>
							No equipment usage in this date range.
						</Typography>
					) : (
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Date</TableCell>
									<TableCell>Equipment</TableCell>
									<TableCell>Start</TableCell>
									<TableCell>End</TableCell>
									<TableCell>Units used</TableCell>
									<TableCell>Site</TableCell>
									<TableCell>Task</TableCell>
									<TableCell>Staff</TableCell>
									<TableCell>Linked</TableCell>
									<TableCell>Notes</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{rows.map((row) => (
									<TableRow key={row.id}>
										<TableCell>
											{row.usageDate
												? moment(row.usageDate).format(
														"D MMM YYYY"
												  )
												: "—"}
										</TableCell>
										<TableCell>
											{row.equipmentName || "—"}
										</TableCell>
										<TableCell>
											{formatReading(row.startReading)}
										</TableCell>
										<TableCell>
											{formatReading(row.endReading)}
										</TableCell>
										<TableCell>
											{row.unitsUsed}{" "}
											{unitLabel(row.unitType)}
										</TableCell>
										<TableCell>{row.siteName || "—"}</TableCell>
										<TableCell>{row.taskTitle || "—"}</TableCell>
										<TableCell>{row.staffName || "—"}</TableCell>
										<TableCell>
											{formatLinkedUsageSource(row.source) || ""}
										</TableCell>
										<TableCell>{row.notes || "—"}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</Paper>
			</PageContent>
		</Page>
	);
};

export default EquipmentUsageReport;
